/**
 * The profession vocabulary, in the one place every client can reach.
 *
 * WHY IT LIVES HERE. Three clients ask "does this person practise a
 * profession?" — the portal and both React Native apps — and each answered it
 * by spelling out the two professions that happened to exist:
 * `isNotary || isAccountant`, or `role === 'notary' || role === 'accountant'`.
 * None of them wanted to know WHICH profession, and all of them therefore
 * answered NO for a third one. The portal's seven sites were fixed by adding a
 * predicate INSIDE the portal, which fixed the portal and left the identical
 * bug live in two mobile apps that could not import it.
 *
 * So the predicate belongs to the workspace, not to one app. The backend has
 * its own counterpart, `practisesAnyProfessionSql(alias)`, because it needs a
 * SQL string rather than a function; `profession-fallthrough.spec.ts` holds it
 * to the same rule.
 *
 * WHAT A THIRD PROFESSION COSTS WHEN THIS IS GOT WRONG, from surfaces already
 * found: a traductor público routed to the consumer experience, never shown
 * the fiscal warning they owe, and — the one that failed OPEN — skipped by the
 * professional two-factor gate.
 */

/**
 * A profession's stable slug: `'notary'`, `'accountant'`, and whatever the
 * `profession` registry table grows next.
 *
 * DELIBERATELY AN OPEN STRING rather than a union of the two that exist today.
 * A union here would reintroduce exactly the closed set this module exists to
 * remove, and would do it in the one file every client imports — turning one
 * app's bug into a workspace-wide one. Where a client genuinely must branch
 * per profession (copy, icons, routing), it should narrow locally and keep a
 * default arm that passes the slug through rather than coercing it to a known
 * value.
 */
export type ProfessionSlug = string;

/**
 * The minimum a session payload has to carry to answer the question.
 *
 * Structural rather than a concrete User type, because each client hand-writes
 * its own session shape — the two apps gate on different things — and this
 * predicate must not force them to agree on anything but this one field.
 */
export interface ProfessionHolder {
  profession?: ProfessionSlug | null;
}

/**
 * Does this person practise a profession on the platform?
 *
 * WHY `profession` AND NOT THE BOOLEANS. `isNotary` / `isAccountant` survive in
 * the API on purpose (spec 781713da, D5): shipped mobile builds read them and
 * cannot be force-updated. They are deliberately NEVER widened, so a third
 * profession appears in `profession` and in neither boolean — the backend's own
 * session payload says exactly that beside the fields. Any client reading the
 * booleans to answer THIS question is therefore wrong by construction, however
 * many professions existed when it was written.
 *
 * A BLANK STRING IS NOT A PROFESSION. The session carries `string | null`, and
 * an empty or whitespace value means the same thing as null — it must not read
 * as "practises something unnamed", which is how a closed-set bug comes back
 * wearing an open-set shape.
 */
export const practisesAnyProfession = (
  user: ProfessionHolder | null | undefined,
): boolean => typeof user?.profession === 'string' && user.profession.trim().length > 0;
